import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { TourCard, ackGoalTourSteps, markGoalTourDone } from './GoalTourGuide';
import { setTour } from '../tour/tourState';

/*
 * First-build theater. While a goal's FIRST build runs, the page stops
 * being a dashboard and becomes a show: it auto-advances through the
 * build's acts — driver funnel -> driver reveal -> model tournament ->
 * the upgraded forecast — switching sections itself and narrating each
 * one with a coach card. This is where the user actually SEES what a
 * goal buys them: candidates being tested, drivers surviving, models
 * racing the no-drivers baseline, the champion going live.
 *
 * It runs INSTEAD of the standard goal tour (which is suspended while
 * the theater is active) and hands off to it when the show ends: the
 * sections the theater already walked are pre-acknowledged, so the tour
 * resumes at Scenarios. Clicking any other section mid-show dismisses
 * the theater — the user has taken the wheel, never fight them for it.
 *
 * Driven purely by the goal's live activity phases (websocket-refreshed);
 * no LLM, no polling, no credits.
 */

const theaterKey = (goalId) => `predictai.theater.goal.${goalId}`;
const readTheaterDone = (goalId) => {
  try { return localStorage.getItem(theaterKey(goalId)) === '1'; } catch (_e) { return true; }
};
const writeTheaterDone = (goalId) => {
  try { localStorage.setItem(theaterKey(goalId), '1'); } catch (_e) { /* private mode */ }
};

// Let the driver reveal breathe before moving on to the tournament.
const REVEAL_DWELL_MS = 12000;
// After WE navigate, ignore section changes briefly so the route landing
// isn't mistaken for the user steering away.
const GRACE_MS = 2500;

const ACTS = [
  {
    id: 'funnel',
    section: 'drivers',
    title: 'Act 1 · Testing your signals',
    text: (ctx) => `Discovery is engineering${ctx.candidates ? ` ~${ctx.candidates}` : ''} candidate features from your signals and testing them against ${ctx.target}. Correlation isn't enough — only features that improve real out-of-sample prediction survive the funnel below. I'll move you along as each stage finishes.`,
  },
  {
    id: 'reveal',
    section: 'drivers',
    title: 'Act 2 · Your drivers',
    text: (ctx) => `${ctx.driversLabel} made it through. The chart at the top shows your strongest driver overlaid on ${ctx.target}, shifted by its lead time — that early movement is what your forecast will lean on.`,
  },
  {
    id: 'tournament',
    section: 'models',
    title: 'Act 3 · The model tournament',
    text: (ctx) => `Models are now racing on your validated drivers — gradient trees, neural nets and foundation models, all scored on the same walk-forward folds. The "Baseline — no drivers" row is the zero-shot forecast from chat: everyone else was picked for ${ctx.target}'s traits and has to beat it.`,
  },
  {
    id: 'forecast',
    section: 'forecast',
    title: 'Act 4 · Your upgraded forecast',
    text: () => 'The champion is live and serving. Its edge over the no-drivers baseline is on the Models tab, and from here it retrains itself as new data arrives. One more thing worth trying: move a driver and watch the forecast react.',
  },
];

/**
 * Drives the first-build show. Returns the floating narration card (or
 * null), the nav key to pulse, and whether the theater currently owns
 * the stage (used to suspend the standard tour).
 */
export const useBuildTheater = ({ goalId, section, activity, goal, runDoc }) => {
  const navigate = useNavigate();
  const [engaged, setEngaged] = useState(null);  // null = undecided
  const [actIdx, setActIdx] = useState(0);
  const [, setTick] = useState(0);               // dwell re-evaluation
  const lastNavActRef = useRef(-1);
  const revealAtRef = useRef(0);
  const graceUntilRef = useRef(0);

  // Re-scope when the user lands on a different goal.
  useEffect(() => {
    setEngaged(null);
    setActIdx(0);
    lastNavActRef.current = -1;
    revealAtRef.current = 0;
    graceUntilRef.current = 0;
  }, [goalId]);

  const phases = useMemo(() => activity?.phases || [], [activity]);
  const phase = useCallback(
    (id) => phases.find((p) => p.id === id)?.state || 'pending',
    [phases],
  );
  const stats = activity?.stats || {};
  const driversValidated = stats.drivers?.validated ?? 0;
  const driversExploratory = stats.drivers?.exploratory ?? 0;
  const discoverDone = phase('discover') === 'done';
  const fitStarted = ['active', 'review', 'done'].includes(phase('fit'));
  const servingReady = Boolean(stats.deploy?.live)
    || ['serving', 'live'].includes(goal?.status);

  // Engage exactly once per goal: first sight of a live (unfinished)
  // build on a goal whose show hasn't played yet. A goal that arrives
  // already serving belongs to the standard tour, not the theater.
  useEffect(() => {
    if (engaged !== null) return;
    if (!goal || !activity) return;
    if (goal.goal_kind === 'fleet' || goal.status === 'failed'
        || readTheaterDone(goalId) || servingReady) {
      setEngaged(false);
      return;
    }
    // Join the show at whatever act the build has reached.
    setActIdx(fitStarted && discoverDone ? 2 : discoverDone ? 1 : 0);
    graceUntilRef.current = Date.now() + GRACE_MS;
    setEngaged(true);
  }, [engaged, goal, activity, servingReady, fitStarted, discoverDone, goalId]);

  const running = Boolean(engaged) && Boolean(goal) && goal.status !== 'failed';

  // Advance acts as the build's phases flip (monotonic, never backwards).
  useEffect(() => {
    if (!running) return undefined;
    let target = 0;
    if (servingReady) {
      target = 3;
    } else if (discoverDone && fitStarted) {
      const elapsed = Date.now() - (revealAtRef.current || 0);
      if (actIdx < 2 && elapsed < REVEAL_DWELL_MS) {
        const t = setTimeout(() => setTick((n) => n + 1), REVEAL_DWELL_MS - elapsed + 50);
        target = 1;
        if (target > actIdx) setActIdx(target);
        return () => clearTimeout(t);
      }
      target = 2;
    } else if (discoverDone) {
      target = 1;
    }
    if (target > actIdx) setActIdx(target);
    return undefined;
  }, [running, servingReady, fitStarted, discoverDone, actIdx]);

  // Each act change moves the stage: navigate to the act's section once.
  useEffect(() => {
    if (!running) return;
    if (lastNavActRef.current === actIdx) return;
    lastNavActRef.current = actIdx;
    graceUntilRef.current = Date.now() + GRACE_MS;
    if (actIdx >= 1 && !revealAtRef.current) revealAtRef.current = Date.now();
    const seg = ACTS[actIdx].section;
    if (section !== seg) navigate(`/goals/${goalId}/${seg}`);
  }, [running, actIdx, section, goalId, navigate]);

  // The user steering to a different section IS the dismissal gesture —
  // hand the page back without ceremony (the standard tour stays
  // available for later).
  useEffect(() => {
    if (!running) return;
    if (section === ACTS[actIdx].section) return;
    if (Date.now() < graceUntilRef.current) return;
    writeTheaterDone(goalId);
    setEngaged(false);
  }, [running, section, actIdx, goalId]);

  // Act 4's button: close the show and hand off to the standard tour at
  // Scenarios (the acts already covered are pre-acknowledged).
  const finishWalk = useCallback(() => {
    writeTheaterDone(goalId);
    ackGoalTourSteps(goalId, ['drivers', 'models', 'forecast']);
    setEngaged(false);
    navigate(`/goals/${goalId}/scenarios`);
  }, [goalId, navigate]);

  // Skip = "stop guiding me": kills the theater AND the tours for good,
  // same contract as the standard tour's skip.
  const skipAll = useCallback(() => {
    writeTheaterDone(goalId);
    markGoalTourDone();
    setTour(null);
    setEngaged(false);
  }, [goalId]);

  if (!running) {
    return { theaterCard: null, theaterHighlight: null, theaterActive: false };
  }

  const act = ACTS[actIdx];
  const candidates = Number(runDoc?.stages?.generate?.out_count)
    || Number(runDoc?.stages?.screen?.in_count) || null;
  const ctx = {
    target: goal?.target_key ? `"${goal.target_key}"` : 'your target',
    candidates: candidates ? candidates.toLocaleString() : null,
    driversLabel: driversValidated
      ? `${driversValidated} signal${driversValidated === 1 ? '' : 's'} earned driver status${driversExploratory ? ` (+${driversExploratory} suggestive)` : ''}`
      : 'The strongest signals',
  };

  const card = (
    <TourCard
      key={act.id}
      title={act.title}
      text={act.text(ctx)}
      progress={{ idx: actIdx, total: ACTS.length }}
      onSkip={skipAll}
      action={actIdx === ACTS.length - 1 ? {
        label: 'Try a what-if',
        icon: <ArrowForwardIcon sx={{ fontSize: 15 }} />,
        onClick: finishWalk,
      } : null}
    />
  );

  return { theaterCard: card, theaterHighlight: act.section, theaterActive: true };
};

export default useBuildTheater;
